import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';

function Analytics({ selectedCompany, apiBaseUrl, apiKey }) {
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTrackingData();
  }, [selectedCompany, apiBaseUrl, apiKey]);

  const fetchTrackingData = async () => {
    if (!selectedCompany) {
      setTrackingData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const companyLower = selectedCompany.toLowerCase();
      const usePostgresStats = companyLower === 'smartmed_2' || companyLower === 'uki_clinic_almaty';

      if (usePostgresStats) {
        if (!apiBaseUrl || !apiKey) {
          throw new Error('API configuration is missing to fetch stats.');
        }
        // Clean endpoint paths: /smartmed_2/ or /uki_clinic_almaty/
        const response = await fetch(`${apiBaseUrl}/${selectedCompany}/stats`, {
          headers: { 'X-Access-Token': apiKey }
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
        }
        const stats = await response.json();

        // Fetch additional tracking metrics from Firebase
        const now = new Date();
        const monthNames = ["January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"];
        const currentMonth = `${monthNames[now.getMonth()]}_${now.getFullYear()}`;

        const trackingRef = doc(db, `production/${selectedCompany}/tracking/${currentMonth}`);
        const trackingSnap = await getDoc(trackingRef);

        const trackingMetrics = trackingSnap.exists() ? trackingSnap.data() : {};

        setTrackingData({
          dialog_count: stats.dialog_count,
          message_count: stats.message_count,
          isSmartMed: true,
          last_updated: trackingMetrics.last_updated || new Date().toISOString(),
          // All uki_clinic_almaty and smartmed_2 metrics from Firebase tracking
          appointment_conversion_rate: trackingMetrics.appointment_conversion_rate,
          appointment_count: trackingMetrics.appointment_count,
          hesitant_count: trackingMetrics.hesitant_count,
          rejection_count: trackingMetrics.rejection_count,
          true_conversion_rate: trackingMetrics.true_conversion_rate,
          total_conversations: trackingMetrics.total_conversations,
          last_audit: trackingMetrics.last_audit
        });
      } else {
        const now = new Date();
        const monthNames = ["January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"];
        const currentMonth = `${monthNames[now.getMonth()]}_${now.getFullYear()}`;

        const docRef = doc(db, `production/${selectedCompany}/tracking/${currentMonth}`);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          let callCountToDisplay = 0;
          let usageToDisplay = 0;

          if (selectedCompany === 'kaiser') {
            callCountToDisplay = data.outbound_call_count !== undefined ? data.outbound_call_count : (data.call_count || 0);
            usageToDisplay = data.outbound_usage !== undefined ? data.outbound_usage : (data.usage || 0);
          } else {
            callCountToDisplay = data.call_count || 0;
            usageToDisplay = data.usage || 0;
          }
          setTrackingData({
            ...data,
            call_count: callCountToDisplay,
            usage: usageToDisplay,
            quota: data.quota !== undefined ? data.quota : 0,
            isSmartMed: false
          });
        } else {
          setTrackingData({
            call_count: 0,
            usage: 0,
            outbound_call_count: 0,
            outbound_usage: 0,
            quota: 0,
            last_updated: "No data available for this month",
            isSmartMed: false
          });
        }
      }
    } catch (err) {
      console.error('Error fetching tracking data:', err);
      setError(`Failed to load tracking data: ${err.message}`);
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-4">Loading tracking data...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-600">{error}</div>;
  }

  if (!selectedCompany) {
    return <div className="p-4">Please select a company to view metrics.</div>;
  }

  if (!trackingData) {
    return <div className="p-4">No tracking data available for {selectedCompany}.</div>;
  }

  const companyDisplayName = selectedCompany.charAt(0).toUpperCase() + selectedCompany.slice(1);

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Usage Metrics for {companyDisplayName}</h3>
        
        {trackingData.isSmartMed ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="p-4 border rounded-lg bg-blue-50">
                <h4 className="text-sm font-medium text-gray-500">New Dialogs this month</h4>
                <p className="mt-1 text-3xl font-semibold text-gray-900">{trackingData.dialog_count !== undefined ? trackingData.dialog_count : 'N/A'}</p>
              </div>
              <div className="p-4 border rounded-lg bg-green-50">
                <h4 className="text-sm font-medium text-gray-500">Total Messages this month</h4>
                <p className="mt-1 text-3xl font-semibold text-gray-900">{trackingData.message_count !== undefined ? trackingData.message_count : 'N/A'}</p>
              </div>
            </div>

            <div className="mt-6 border-t pt-6">
              <h4 className="text-md font-medium text-gray-700 mb-4">Additional Metrics</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard label="Total Conversations" value={trackingData.total_conversations} />
                <MetricCard label="Appointment Count" value={trackingData.appointment_count} />
                <MetricCard label="Appointment Conversion Rate" value={trackingData.appointment_conversion_rate} unit="%" />
                <MetricCard label="Appointment Conversion Rate (Audited)" value={trackingData.true_conversion_rate != null ? trackingData.true_conversion_rate.toFixed(1) : null} unit="%" />
                <MetricCard label="Hesitant Count" value={trackingData.hesitant_count} />
                <MetricCard label="Rejection Count" value={trackingData.rejection_count} />
              </div>
              {trackingData.last_audit && (
                <p className="mt-4 text-sm text-gray-500">
                  Last audit: {new Date(trackingData.last_audit).toLocaleString()}
                </p>
              )}
            </div>
          </>
        ) : (
          <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="p-4 border rounded-lg bg-blue-50">
            <h4 className="text-sm font-medium text-gray-500">
              {selectedCompany === 'kaiser' ? 'Total Outbound Calls' : 'Total Calls'} this month
            </h4>
                <p className="mt-1 text-3xl font-semibold text-gray-900">{trackingData.call_count || 0}</p>
          </div>
          <div className="p-4 border rounded-lg bg-green-50">
            <h4 className="text-sm font-medium text-gray-500">
              {selectedCompany === 'kaiser' ? 'Total Outbound Minutes' : 'Total Minutes'} this month
            </h4>
                <p className="mt-1 text-3xl font-semibold text-gray-900">{(trackingData.usage || 0).toFixed(2)}</p>
          </div>
        </div>
            {(trackingData.quota || 0) > 0 && (
          <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-500 mb-1">Usage quota ({trackingData.quota} minutes)</h4>
            <div className="w-full bg-gray-200 rounded-full h-2.5">
              <div 
                    className={`h-2.5 rounded-full ${((trackingData.usage || 0) / (trackingData.quota || 1) * 100) > 90 ? 'bg-red-600' : 'bg-blue-600'}`}
                    style={{ width: `${Math.min(((trackingData.usage || 0) / (trackingData.quota || 1) * 100), 100)}%` }}
              ></div>
            </div>
            <p className="mt-1 text-sm text-gray-500">
                  {(trackingData.usage || 0).toFixed(2)} / {trackingData.quota} minutes ({((trackingData.usage || 0) / (trackingData.quota || 1) * 100).toFixed(1)}%)
            </p>
          </div>
            )}
          </>
        )}
        
        <p className="mt-4 text-sm text-gray-500">
          Last updated: {trackingData.last_updated ? (
            trackingData.isSmartMed
              ? (typeof trackingData.last_updated === 'string' ? trackingData.last_updated : new Date(trackingData.last_updated).toLocaleString())
              : (trackingData.last_updated.seconds ? new Date(trackingData.last_updated.seconds * 1000).toLocaleString() : new Date(trackingData.last_updated).toLocaleString())
          ) : 'N/A'}
        </p>
      </div>
    </div>
  );
}

const MetricCard = ({ label, value, unit = '' }) => (
  <div className="p-3 border rounded-lg bg-gray-50">
    <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</h5>
    <p className="mt-1 text-2xl font-semibold text-gray-900">
      {value !== undefined && value !== null ? `${value}${unit}` : 'N/A'}
    </p>
  </div>
);

export default Analytics;